<template>
    <div class="form-group" :class="formGroupClass">
        <!-- Label on left side if not a right-side label (for checkboxes, radios and booleans) -->
        <label
            v-if="label && !isRightLabel"
            class="col-form-label d-flex justify-content-between align-items-center"
            :class="labelClass"
        >
            <span>{{ label }}{{ required ? '*' : '' }}</span>
            <ToolTip
                v-if="params?.tooltipContent"
                align="start"
                :max-width="labelTooltipMaxWidth"
            >
                <template #tooltip-trigger>
                    <i class="fi fi-info tooltip-icon" aria-hidden="true"></i>
                </template>
                <template #tooltip-content>
                    {{ params.tooltipContent }}
                </template>
            </ToolTip>
        </label>

        <ToolTip align="start">
            <template #tooltip-trigger>
                <div
                    :class="[
                        { 'position-relative': $slots['input-button'] },
                        fieldClass,
                    ]"
                >
                    <div
                        class="field-tooltip-anchor"
                        data-tooltip-anchor
                        :class="{
                            'field-tooltip-anchor--compact':
                                tooltipAnchorCompact,
                        }"
                    >
                        <!-- Select / Multi-select using MultiselectItem -->
                        <template
                            v-if="
                                this.type === 'select' ||
                                this.type === 'multi-select'
                            "
                        >
                            <MultiselectItem
                                :label="label"
                                :label-position="labelPosition"
                                :aria-label="params?.ariaLabel || undefined"
                                :options="paramOptions"
                                :model-value="localMultiselectValue"
                                :type="
                                    this.type === 'multi-select'
                                        ? 'multiselect'
                                        : 'single-select'
                                "
                                :placeholder-text="selectPlaceholderText"
                                :search-placeholder="params?.searchPlaceholder"
                                :required="required"
                                :disabled="disabled"
                                :loading="isLoading"
                                :error-message="invalidError"
                                :external-invalid="isInvalid"
                                :external-error-id="errorId"
                                :allow-create="params?.allowCreate ?? false"
                                :allow-empty="params?.allowEmpty !== false"
                                :plain-text-mode="plainTextMode"
                                @update:model-value="handleMultiselectUpdate"
                                @create-option="handleCreateOption"
                                @remove-option="handleRemoveOption"
                            />
                        </template>

                        <!-- Boolean field rendered as a toggle switch -->
                        <template v-else-if="type === 'boolean'">
                            <ToggleField
                                v-model="localValue"
                                :disabled="disabled"
                                :text="params?.text || label"
                                @change="localValueChange"
                            ></ToggleField>
                        </template>

                        <!-- Radio group -->
                        <template v-else-if="type === 'radio'">
                            <RadioGroup
                                :options="params.options"
                                :name="fieldName"
                                :stacked="params.stacked"
                                v-model="localValue"
                                :error="invalidError"
                                :required="required"
                                :disabled="disabled"
                                :showIndicators="disabled"
                                @change="localValueChange"
                                @remove-option="handleRemoveOption"
                            />
                        </template>

                        <!-- Checkbox Group -->
                        <template v-else-if="type === 'checkbox-group'">
                            <CheckboxGroup
                                :options="params.options"
                                :name="fieldName"
                                v-model="localValue"
                                :error="invalidError"
                                :required="required"
                                :disabled="disabled"
                                @change="localValueChange"
                                @remove-option="handleRemoveOption"
                            />
                        </template>

                        <!-- Checkbox -->
                        <template v-else-if="type === 'checkbox'">
                            <div class="form-check d-flex align-items-center">
                                <input
                                    class="form-check-input"
                                    :type="type"
                                    :value="params?.value || true"
                                    :id="params?.name || label"
                                    v-model="localValue"
                                    :required="required"
                                    :disabled="disabled"
                                    @invalid.prevent="handleInvalid"
                                    @input="handleInput"
                                />
                                <label
                                    :for="params?.name || label"
                                    style=""
                                    class="form-check-label"
                                >
                                    {{ label }}
                                    <span v-if="required">*</span>
                                </label>
                            </div>
                        </template>

                        <!-- Textarea -->
                        <template v-else-if="type === 'textarea'">
                            <textarea
                                ref="theInput"
                                class="form-control textarea-control"
                                :class="{ 'input-invalid': isInvalid }"
                                :aria-label="params?.ariaLabel || undefined"
                                :aria-invalid="isInvalid ? 'true' : undefined"
                                :aria-describedby="
                                    isInvalid ? errorId : undefined
                                "
                                :disabled="disabled"
                                :required="required"
                                :placeholder="params?.placeholder || ''"
                                :maxlength="params?.maxlength || null"
                                :style="inputStyle"
                                v-model="localValue"
                                @input="handleInput"
                                @click="localValueChange($event.target.value)"
                                @change="localValueChange($event.target.value)"
                                @invalid.prevent="handleInvalid"
                            ></textarea>
                        </template>

                        <template v-else-if="type === 'range'">
                            <component
                                :is="RangeSlider"
                                v-model="localValue"
                                :disabled="disabled"
                                :error="invalidError"
                                :required="required"
                                :min="params?.min"
                                :max="params?.max"
                                :step="params?.step"
                                @update:model-value="localValueChange"
                            />
                        </template>

                        <!-- Date picker -->
                        <template v-else-if="type === 'date'">
                            <component
                                :is="DatePicker"
                                v-model="localValue"
                                :disabled="disabled"
                                :error="invalidError"
                                :external-invalid="isInvalid"
                                :error-id="errorId"
                                :required="required"
                                :placeholder="params?.placeholder"
                                :params="params"
                                @update:model-value="localValueChange"
                            />
                        </template>

                        <template v-else-if="type === 'time-picker'">
                            <component
                                :is="TimePicker"
                                v-model="localValue"
                                :disabled="disabled"
                                :error="invalidError"
                                :required="required"
                                :placeholder="params?.placeholder"
                                :params="params"
                                @update:model-value="localValueChange"
                            />
                        </template>

                        <!-- All other types (standard input field) -->
                        <template v-else>
                            <div class="position-relative">
                                <input
                                    ref="theInput"
                                    :name="fieldName"
                                    class="form-control"
                                    :class="{
                                        'input-invalid': isInvalid,
                                        'date-input-placeholder':
                                            type === 'date' && localValue == '',
                                        'number-input--no-controls':
                                            type === 'number' &&
                                            params?.hideNumberControls,
                                    }"
                                    :type="type"
                                    :aria-invalid="
                                        isInvalid ? 'true' : undefined
                                    "
                                    :aria-describedby="
                                        isInvalid ? errorId : undefined
                                    "
                                    :aria-label="params?.ariaLabel || undefined"
                                    :disabled="disabled"
                                    :required="required"
                                    :placeholder="params?.placeholder || ''"
                                    :pattern="
                                        type === 'tel'
                                            ? '^[\\+]?[0-9\\s\\-\\(\\)\\.]{7,20}$'
                                            : params?.pattern || null
                                    "
                                    :maxlength="params?.maxlength || null"
                                    :min="params?.min || null"
                                    :max="
                                        type === 'date'
                                            ? '9999-12-31'
                                            : params?.max || null
                                    "
                                    :step="params?.step || null"
                                    :autocomplete="params?.autocomplete || null"
                                    @invalid.prevent="handleInvalid"
                                    @input="handleInput"
                                    @wheel="handleWheel"
                                    @click="
                                        localValueChange($event.target.value)
                                    "
                                    @change="
                                        localValueChange($event.target.value)
                                    "
                                    :style="{
                                        paddingRight: icon
                                            ? '2rem'
                                            : 'var(--space-3)',
                                        ...inputStyle,
                                    }"
                                    v-model="localValue"
                                />
                                <!-- Optional icon which triggers a custom event -->
                                <i
                                    v-if="icon"
                                    :class="[
                                        'fi',
                                        'position-absolute',
                                        'top-50',
                                        'end-0',
                                        'translate-middle-y',
                                        'me-2',
                                        'icon-class',
                                        icon,
                                    ]"
                                    style="cursor: pointer"
                                    aria-hidden="true"
                                    @click="handleIconClick"
                                ></i>
                            </div>
                        </template>

                        <!-- Slot for adding custom content after the input -->
                        <slot name="input-button"></slot>
                    </div>
                </div>
            </template>
            <template v-if="disabled && disabledTooltip" #tooltip-content>
                {{ disabledTooltip }}
            </template>
        </ToolTip>

        <div
            v-if="
                isInvalid &&
                type !== 'select' &&
                type !== 'multi-select' &&
                type !== 'date'
            "
            :id="errorId"
            class="font-note"
            style="color: var(--danger)"
        >
            {{ invalidError }}
        </div>
    </div>
</template>

<script>
/**
 * @component Field
 * @description A versatile form field component that renders different types of inputs based on the type prop.
 *
 * @example
 * ```vue
 * <Field
 *   label="Username"
 *   type="text"
 *   v-model="username"
 *   :required="true"
 *   :params="{ placeholder: 'Enter username' }"
 * />
 * ```
 *
 * @typedef {Object} FieldParams
 * @property {string} [placeholder] - Placeholder text for the input
 * @property {string} [ariaLabel] - Accessible label when no visible label is rendered
 * @property {string} [pattern] - Regex pattern for validation (for text inputs)
 * @property {number} [maxlength] - Maximum length for text inputs
 * @property {string} [tooltipContent] - Content to show in the tooltip
 * @property {number} [tooltipMaxWidth] - Max tooltip width in rem (label tooltips default to 18.75)
 * @property {string} [name] - Name attribute for the input (required for radio/checkbox)
 * @property {Array|string} [options] - Options for select/multi-select/radio. Can be array of objects or 'countries'|'provinces'|'states'
 * @property {string} [text] - Text to display for boolean toggle
 * @property {string} [searchPlaceholder] - Placeholder for search in multiselect
 * @property {any} [value] - Value for checkbox input
 * @property {boolean} [hideNumberControls] - Hide browser spinner controls for number inputs
 * @property {boolean} [disableWheel] - Prevent mouse wheel changes on number inputs
 *
 * @prop {string} label - The field's label text
 * @prop {any} modelValue - The v-model value. Type varies based on field type:
 *   - text/textarea/email/tel: string
 *   - number: number
 *   - date: string (YYYY-MM-DD format)
 *   - boolean: boolean
 *   - select: string|number (single value)
 *   - multi-select: Array<string|number>
 *   - checkbox: boolean|any (based on params.value)
 *   - radio: string|number
 * @prop {string} type - The type of field to render. Supported types:
 *   - Standard HTML types: 'text', 'number', 'email', 'tel', 'date', 'password'
 *   - 'textarea': Multi-line text input
 *   - 'select': Single-select dropdown
 *   - 'multi-select': Multi-select dropdown
 *   - 'boolean': Toggle switch
 *   - 'radio': Radio button group
 *   - 'checkbox': Single checkbox
 * @prop {FieldParams} [params] - Additional configuration parameters
 * @prop {boolean} [disabled=false] - Whether the field is disabled
 * @prop {boolean} [loading=false] - Whether the field is loading and temporarily disabled
 * @prop {boolean} [required=false] - Whether the field is required
 * @prop {string} [invalidMessage] - Custom validation message. A non-empty value also marks the field invalid,
 *   so pass an empty value while the field is valid. If not provided, defaults based on type:
 *   - tel: "Please enter a valid phone number"
 *   - email: "Please enter a valid email address"
 *   - date: "Please enter a valid date"
 *   - number: "Please enter a valid integer"
 *   - multi-select: "Please select at least one option"
 *   - select: "Please select an option"
 *   - others: "Required"
 * @prop {string} [colClass] - Bootstrap column class for layout. Examples:
 *   - "col-3": Label takes 3 columns, field takes 9
 *   - "col": Full width
 *   - "col-auto": Auto width
 * @prop {string} [icon] - Optional icon class name (using fi icon set)
 *
 * @emits {any} update:modelValue - When the field value changes
 * @emits {any} change - On field value change
 * @emits {any} click - On field click
 * @emits {void} open - When dropdown/select opens (for select types)
 * @emits {void} close - When dropdown/select closes
 * @emits {void} remove - When an item is removed (multi-select)
 * @emits {(value: any, event: Event)} icon-click - When the optional icon is clicked
 *
 * @example Select/Multi-select Usage
 * ```vue
 * <!-- Single Select -->
 * <Field
 *   type="select"
 *   label="Country"
 *   v-model="country"
 *   :params="{
 *     options: 'countries',  // or custom array of {label, value} objects
 *     placeholder: 'Select a country',
 *     searchPlaceholder: 'Search countries'
 *   }"
 * />
 *
 * <!-- Multi Select -->
 * <Field
 *   type="multi-select"
 *   label="Skills"
 *   v-model="selectedSkills"
 *   :params="{
 *     options: [
 *       { label: 'JavaScript', value: 'js' },
 *       { label: 'Python', value: 'py' }
 *     ],
 *     placeholder: 'Select skills'
 *   }"
 * />
 * ```
 *
 * @example Radio/Checkbox Usage
 * ```vue
 * <!-- Radio Group -->
 * <Field
 *   type="radio"
 *   label="Gender"
 *   v-model="gender"
 *   :params="{
 *     name: 'gender',
 *     options: [
 *       { label: 'Male', value: 'male' },
 *       { label: 'Female', value: 'female' }
 *     ]
 *   }"
 * />
 *
 * <!-- Checkbox -->
 * <Field
 *   type="checkbox"
 *   label="Accept Terms"
 *   v-model="accepted"
 *   :params="{
 *     name: 'terms',
 *     value: true
 *   }"
 * />
 * ```
 */

import MultiselectItem from './fields/multiselect/MultiselectItem.vue';
import ToggleField from './fields/ToggleField.vue';
import RadioGroup from './fields/RadioGroup.vue';
import CheckboxGroup from './fields/CheckboxGroup.vue';
import { defineAsyncComponent } from 'vue';

import {
    countryOptions,
    provinceOptions,
    stateOptions,
    yearOptions,
} from '@/services/constants';
import ToolTip from './fields/ToolTip.vue';

let nextFieldErrorId = 0;

export default {
    name: 'Field',
    components: {
        MultiselectItem,
        ToggleField,
        RadioGroup,
        CheckboxGroup,
        ToolTip,
    },
    props: {
        label: {
            type: String,
            default: '',
        },
        modelValue: {
            type: null,
            default: '',
        },
        type: {
            type: String,
            default: 'text',
        },
        params: {
            type: Object,
            default: () => ({}),
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        loading: {
            type: Boolean,
            default: false,
        },
        disabledTooltip: {
            type: String,
            default: '',
        },
        required: {
            type: Boolean,
            default: false,
        },
        invalidMessage: {
            type: String,
            default: null,
        },
        colClass: {
            type: [String, null],
            default: null,
        },
        icon: {
            type: String,
            default: '',
        },
        inputStyle: {
            type: Object,
            default: () => ({}),
        },
        plainTextMode: {
            type: Boolean,
            default: false,
        },
        labelPosition: {
            type: String,
            default: 'top',
            validator: (value) => ['top', 'left'].includes(value),
        },
    },
    emits: [
        'update:modelValue',
        'change',
        'click',
        'open',
        'close',
        'remove',
        'icon-click',
        'create-option',
        'remove-option',
    ],
    data() {
        return {
            localValue: null,
            localMultiselectValue: {},
            isInvalid: false,
            errorId: `field-error-${++nextFieldErrorId}`,
        };
    },
    computed: {
        fieldName() {
            // Slugify the label.  Lets browsers auto-parse some fields
            if (this.params?.name) {
                return this.params.name;
            }

            return this.label
                ? this.label.replaceAll(' ', '-').toLowerCase()
                : '';
        },
        labelTooltipMaxWidth() {
            const width = Number(this.params?.tooltipMaxWidth);
            return Number.isFinite(width) && width > 0 ? width : 18.75;
        },

        /**
         * Returns the available options for select/multi-select fields.
         * Handles both custom options arrays and predefined option sets (countries, provinces, states).
         * @returns {Array<{label: string, value: string|number}>} Array of option objects
         */
        paramOptions() {
            if (this.params && 'options' in this.params) {
                const options = this.params.options;
                if (options === 'countries') return countryOptions;
                else if (options === 'provinces') return provinceOptions;
                else if (options === 'states') return stateOptions;
                else if (options === 'years')
                    return yearOptions(
                        this.params?.startYear,
                        this.params?.endYear,
                        this.params?.yearInterval
                    );
                return options;
            }
            return [];
        },

        isLoading() {
            return this.loading || this.params?.loading === true;
        },

        selectPlaceholderText() {
            if (this.isLoading) {
                return (
                    this.params?.loadingText || this.$t('common.states.loading')
                );
            }

            return (
                this.params?.placeholder ??
                this.$t('fields.multiselect.selectAnyOption')
            );
        },

        /**
         * Determines the wrapper class for the form group based on colClass.
         * @returns {string} CSS class string
         */
        formGroupClass() {
            return this.colClass && this.colClass.includes('col')
                ? 'row g-0'
                : 'd-flex flex-column';
        },

        /**
         * Returns the label class based on colClass.
         * @returns {string} CSS class string
         */
        labelClass() {
            return this.colClass || '';
        },

        /**
         * Computes the field's column class if a valid colClass is given.
         * @returns {string} CSS class string
         */
        fieldClass() {
            return this.computeColClass(this.colClass);
        },

        tooltipAnchorCompact() {
            return ['checkbox', 'checkbox-group', 'boolean'].includes(
                this.type
            );
        },

        /**
         * Determines if the label should be placed on the right side of the field.
         * @returns {boolean} True for radio, checkbox, boolean, and select fields
         */
        isRightLabel() {
            return ['checkbox', 'boolean', 'multi-select', 'select'].includes(
                this.type
            );
        },

        /**
         * Returns the appropriate validation error message based on field type.
         * @returns {string} Error message
         */
        invalidError() {
            if (this.invalidMessage) {
                return this.invalidMessage;
            }

            if (this.type === 'tel') {
                return this.$t('validation.invalidPhone');
            }
            if (this.type === 'email') {
                return this.$t('validation.invalidEmail');
            }
            if (this.type === 'date') {
                return this.$t('validation.invalidDate');
            }
            if (this.type === 'number') {
                return this.$t('validation.invalidInteger');
            }
            if (
                this.type === 'multi-select' ||
                this.type === 'checkbox-group'
            ) {
                return this.$t('validation.selectAtLeastOne');
            }
            if (this.type === 'select') {
                return this.$t('validation.selectOption');
            }
            if (this.type === 'range') {
                return this.$t('validation.invalidValue');
            }
            if (this.type === 'time-picker') {
                return this.$t('validation.invalidTime');
            }

            return this.$t('validation.required');
        },

        // Dynamically import DatePicker when type is date in template
        DatePicker() {
            return defineAsyncComponent(() =>
                import('./fields/DatePicker.vue')
            );
        },

        TimePicker() {
            return defineAsyncComponent(() =>
                import('./fields/TimePicker.vue')
            );
        },

        RangeSlider() {
            return defineAsyncComponent(() =>
                import('./fields/RangeSlider.vue')
            );
        },
    },
    watch: {
        modelValue: {
            immediate: true,
            handler(newVal) {
                this.setLocalValue(newVal);
            },
            deep: true,
        },
        required() {
            this.$nextTick(() => {
                if (!this.required) {
                    this.isInvalid = Boolean(this.invalidMessage);
                    return;
                }
                const input = this.$refs.theInput;
                if (input && typeof input.checkValidity === 'function') {
                    this.isInvalid =
                        Boolean(this.invalidMessage) || !input.checkValidity();
                }
            });
        },
        invalidMessage: {
            immediate: true,
            handler(value) {
                this.isInvalid = Boolean(value);
            },
        },
    },
    methods: {
        /**
         * Sets the internal localValue based on type and new value.
         * Handles type-specific value conversions.
         * @param {any} newValue - The new value to set
         */
        setLocalValue(newValue) {
            if (this.type === 'boolean') {
                this.localValue = newValue === true;
            } else if (this.type === 'select' || this.type === 'multi-select') {
                this.localValue = newValue;
                this.initializeMultiselectValue(newValue);
            } else {
                this.localValue = newValue;
            }
        },

        /**
         * Initializes the multiselect value object based on the current modelValue.
         * O(1) membership using a boolean map that only stores selected items.
         * @param {string|string[]} value - Current value(s) for the multiselect
         */
        initializeMultiselectValue(value) {
            if (this.type !== 'select' && this.type !== 'multi-select') {
                return;
            }

            if (this.type === 'select') {
                const key =
                    value == null || value === '' ? null : String(value);
                this.localMultiselectValue = key ? { [key]: true } : {};
                return;
            }

            if (this.type === 'multi-select') {
                // Multi-select: only store selected keys, no false entries
                if (Array.isArray(value) && value.length) {
                    const map = {};
                    value.forEach((v) => {
                        map[String(v)] = true;
                    });
                    this.localMultiselectValue = map;
                } else {
                    this.localMultiselectValue = {};
                }
            }
        },

        /**
         * Handles updates from the MultiselectItem component.
         * Converts the selection state object back to value(s) for v-model.
         * @param {Object<string, boolean>} values - Mapping of option values to their selected state
         */
        handleMultiselectUpdate(values) {
            if (this.type === 'select') {
                const selectedValue = Object.keys(values).find(
                    (key) => values[key] === true
                );
                this.localValueChange(selectedValue || null);
            } else if (this.type === 'multi-select') {
                const selectedValues = Object.keys(values).filter(
                    (key) => values[key] === true
                );
                this.localValueChange(selectedValues);
            }
        },

        // Handles creating a new option
        handleCreateOption(newOptionValue) {
            this.$emit('create-option', newOptionValue);
        },

        handleRemoveOption(optionToRemove) {
            this.$emit('remove-option', optionToRemove);
        },

        focus() {
            this.focusField();
        },

        /**
         * Sets focus to the field's input element.
         */
        focusField() {
            if (this.$refs.theInput) {
                this.$refs.theInput.focus();
                return;
            }
            this.$el
                ?.querySelector?.(
                    'button:not([disabled]), [role="combobox"], input:not([disabled]), select:not([disabled]), textarea:not([disabled])'
                )
                ?.focus?.();
        },

        /**
         * Emits the custom icon click event.
         * @param {Event} event - The click event object
         */
        handleIconClick(event) {
            this.$emit('icon-click', this.modelValue, event);
        },

        /**
         * Emits update, click, and change events when the value changes.
         * @param {any} newValue - The new field value
         */
        localValueChange(newValue) {
            this.$emit('update:modelValue', newValue);
            this.$emit('click', newValue);
            this.$emit('change', newValue);
        },

        /**
         * Computes a new column class for the field based on the provided colClass.
         * For a colClass like 'col-3', returns 'col-9' for the field.
         * @param {string} colClass - The column class to compute from
         * @returns {string} The computed column class
         */
        computeColClass(colClass) {
            if ([null, undefined, '', 'col-auto', 'col'].includes(colClass))
                return colClass;
            if (colClass.startsWith('col')) {
                const tokens = colClass.split('-');
                const labelNum = parseInt(tokens[tokens.length - 1], 10);
                const fieldNum = 12 - labelNum;
                return `${tokens.slice(0, 2).join('-')}-${fieldNum}`;
            }
            return '';
        },

        /**
         * Handles invalid events on the field.
         * Sets the isInvalid flag to true.
         */
        handleInvalid() {
            this.isInvalid = true;
        },

        /**
         * Handles generic input events.
         * Updates validation state and emits value changes.
         * @param {Event} event - The input event object
         */
        handleInput(event) {
            this.isInvalid = !event.target.checkValidity();
            if (this.type === 'checkbox') {
                this.localValueChange(event.target.checked);
            } else {
                this.localValueChange(event.target.value);
            }
        },

        handleWheel(event) {
            if (this.type !== 'number' || !this.params?.disableWheel) return;
            event.preventDefault();
        },

        /**
         * Validates the field and returns whether it's valid.
         * Note: For select/multi-select fields, validation is handled by MultiselectItem.
         * @returns {boolean} Whether the field is valid
         */
        validate() {
            if (this.disabled) return true;

            if (this.type === 'select' || this.type === 'multi-select') {
                return true;
            }

            if (this.$refs.theInput) {
                const isValid = this.$refs.theInput.checkValidity();
                this.isInvalid = !isValid;
                return isValid;
            }

            return !this.isInvalid;
        },
    },
    mounted() {
        this.setLocalValue(this.modelValue);
    },
};
</script>

<style scoped>
.textarea-control {
    resize: none;
    padding: var(--space-3);
    line-height: 1.5;
    min-height: 8.375rem;
    max-height: 12.5rem;
}

.form-group.flex-column {
    gap: var(--space-2);
}

.field-tooltip-anchor {
    display: block;
    width: 100%;
}

.field-tooltip-anchor--compact {
    display: inline-flex;
    width: fit-content;
    max-width: 100%;
    align-items: center;
}

.input-invalid {
    border: 1px solid var(--danger);
}

.number-input--no-controls::-webkit-outer-spin-button,
.number-input--no-controls::-webkit-inner-spin-button {
    margin: 0;
    appearance: none;
    -webkit-appearance: none;
}

.number-input--no-controls {
    appearance: textfield;
    -moz-appearance: textfield;
}

.tooltip-icon {
    cursor: pointer;
    color: var(--primary-400);
    display: inline-block;
    flex-shrink: 0;
}
</style>
